import { format, parseISO, subDays } from 'date-fns'
import { isNil } from 'lodash'

// Banner / progress-bar state derived from headline usage vs contract limit.
// Thresholds match the QCER-777 spec:
//   - <90%       -> within limit, no banner, brand color
//   - >=90% <100% -> approaching, orange banner + bar
//   - >=100%     -> exceeded, red banner + bar

export const USAGE_STATE = {
  WITHIN: 'within',
  APPROACHING: 'approaching',
  EXCEEDED: 'exceeded',
}

export const APPROACHING_THRESHOLD = 0.9

export const getUsageState = (used, limit) => {
  if (!limit || limit <= 0) {
    return USAGE_STATE.WITHIN
  }
  const pct = used / limit
  if (pct >= 1) {
    return USAGE_STATE.EXCEEDED
  }
  if (pct >= APPROACHING_THRESHOLD) {
    return USAGE_STATE.APPROACHING
  }
  return USAGE_STATE.WITHIN
}

export const getUsagePercent = (used, limit) => {
  if (!limit || limit <= 0) {
    return 0
  }
  return used / limit
}

const APPROACHING_MESSAGE =
  "You're approaching your interaction limit. Contact your CSM to discuss upgrading your plan before scoring is interrupted."

export const getBannerCopy = (state, limit) => {
  if (state === USAGE_STATE.APPROACHING) {
    return APPROACHING_MESSAGE
  }
  if (state === USAGE_STATE.EXCEEDED) {
    const formattedLimit = (limit ?? 0).toLocaleString()
    return `You've exceeded your ${formattedLimit} interaction limit. New interactions may not be scored. Contact your CSM to resolve this.`
  }
  return null
}

const CHANNEL_LABEL = {
  email: 'Email',
  sms: 'SMS',
  chat: 'Chat',
}

export const formatChannelLabel = (channel) => CHANNEL_LABEL[channel] ?? channel

/**
 * Render the contract year covered by the numbers on the page.
 *
 * `contractStart` / `contractEnd` are date-only ISO strings ("2026-06-15").
 * `new Date('2026-06-15')` parses as UTC midnight and would render as the
 * previous day for anyone west of UTC, so both ends go through `parseISO`,
 * which treats a date-only string as local time.
 *
 * `contractEnd` is the API's exclusive upper bound (`sent_at < end`), so the
 * last day actually counted is the day before it — that is the date shown, so
 * the label describes exactly the period the totals cover.
 *
 * @param {string} [contractStart] Inclusive first day, ISO date-only.
 * @param {string} [contractEnd] Exclusive last day, ISO date-only.
 * @returns {string|null} e.g. "6/15/2026 – 6/14/2027", or null if either end is
 *   missing (the caller renders nothing rather than half a range).
 */
export const formatContractWindow = (contractStart, contractEnd) => {
  if (isNil(contractStart) || isNil(contractEnd)) {
    return null
  }
  const firstDay = parseISO(contractStart)
  const lastDay = subDays(parseISO(contractEnd), 1)
  return `${format(firstDay, 'M/d/yyyy')} – ${format(lastDay, 'M/d/yyyy')}`
}
